import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";
import { getJobById, deleteJob } from "../../services/jobsService";
import { toast } from "react-toastify";
import { useJobContext } from "../../context/JobContext";
import { updateLeadStatus } from "../../services/leadsService";
import { useSelector } from 'react-redux';
import { editUserProfile } from "../../services/userService";
import Spinner from "../../utils/Spinner";
import { getImage } from "../../utils/cloudinarySizing";
import LeadCard from "./components/LeadCard"

const RequestDetails = () => {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    const [activeMenuId, setActiveMenuId] = useState(null);
    const [activeDropupId, setActiveDropupId] = useState(null);

    const [brokenImages, setBrokenImages] = useState({});
    const [imageLoaded, setImageLoaded] = useState({});

    const { openEditJob, updatedJob, setUpdatedJob } = useJobContext();

    const [dropupForm, setDropupForm] = useState({
        email: "",
        phone: ""
    });

    const navigate = useNavigate();
    const user_profile = useSelector((state) => state.profile.data);

    // -----------------------------
    // FETCH JOB
    // -----------------------------
    useEffect(() => {
        const fetchJob = async () => {
            try {
                setLoading(true);
                const data = await getJobById(id);
                setJob(data);
            } catch (error) {
                console.error(error);
                toast.error("Failed to load job details.");
            } finally {
                setLoading(false);
            }
        };

        fetchJob();
    }, [id]);

    // -----------------------------
    // SYNC UPDATED JOB
    // -----------------------------
    useEffect(() => {
        if (updatedJob && job && updatedJob.id === job.id) {
            setJob(updatedJob);
            setUpdatedJob(null);
        }
    }, [updatedJob, job, setUpdatedJob]);

    // -----------------------------
    // PRELOAD IMAGE (NO FLICKER)
    // -----------------------------
    useEffect(() => {
        if (!job?.id || !job?.images?.[0]?.image) return;

        const src = getImage(job.images[0].image, "srv_sm");
        if (!src) return;

        const img = new Image();

        img.onload = () => {
            setImageLoaded((prev) => ({
                ...prev,
                [job.id]: true,
            }));
        };

        img.onerror = () => {
            setImageLoaded((prev) => ({
                ...prev,
                [job.id]: false,
            }));

            setBrokenImages((prev) => ({
                ...prev,
                [job.id]: true,
            }));
        };

        img.src = src;
    }, [job?.id, job?.images?.[0]?.image]);

    // -----------------------------
    // UI HELPERS (UNCHANGED LOGIC)
    // -----------------------------
    const toggleMenu = (id) => {
        setActiveMenuId(activeMenuId === id ? null : id);
    };

    const toggleDropup = (id) => {
        setActiveDropupId(activeDropupId === id ? null : id);
    };

    const editRequest = (request) => {
        openEditJob(request);
        setActiveMenuId(null);
    };

    const handleDeleteRequest = async (jobId) => {
        try {
            await deleteJob(jobId);
            toast.success("Request deleted successfully.");
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete the request.");
        }
    };

    const handleLeadAction = async (leadId, status) => {
        try {
            const updatedLead = await updateLeadStatus(leadId, status);

            setJob((prevJob) => ({
                ...prevJob,
                leads: prevJob.leads.map((lead) =>
                    lead.id === leadId
                        ? { ...lead, status: updatedLead.status }
                        : lead
                ),
            }));

            toast.success(
                `Lead ${status === "accepted" ? "accepted" : "rejected"} successfully.`
            );
        } catch (error) {
            console.error(error);
            toast.error("Failed to update lead status.");
        }
    };

    const handleDropupConfirm = async (leadId) => {
        await handleLeadAction(leadId, "accepted");
        toggleDropup(null);
    };

    const activeLeadsCount =
        job?.leads?.filter(
            (lead) => lead.status === "pending" || lead.status === "accepted"
        ).length || 0;

    const allLeadsCount = job?.leads?.length || 0;

    // -----------------------------
    // LOADING STATES
    // -----------------------------
    if (loading) return <Spinner />;
    if (!job) return <div className="empty-state">Job not found.</div>;

    // -----------------------------
    // IMAGE STATE VALUES
    // -----------------------------
    const src = getImage(job.images?.[0]?.image, "srv_sm");
    const isLoaded = imageLoaded[job.id];
    const isBroken = brokenImages[job.id];


    // -----------------------------
    // RENDER
    // -----------------------------
    return (
        <div className="my-requests-page">
            <div className="request-item-detail">
                <div className="request-top-row">

                    {/* THUMBNAIL (Flicker-Free) */}
                    <div
                        className="thumbnail-wrapper"
                        onClick={() => navigate(`/service-requests/${id}`)}
                    >
                        {!src || isBroken || !isLoaded ? (
                            <div className="job-thumbnail placeholder-thumbnail">
                                <span>No Image</span>
                            </div>
                        ) : (
                            <img
                                src={src}
                                alt="Job thumbnail"
                                className="job-thumbnail"
                                onError={() => {
                                    setBrokenImages((prev) => ({
                                        ...prev,
                                        [job.id]: true,
                                    }));
                                }}
                            />
                        )}
                    </div>

                    {/* TITLE */}
                    <div className="request-content">
                        <div className="request-title-row">
                            <h2 className="request-title">{job.title}</h2>

                            <span
                                onClick={() =>
                                    navigate(`/service-requests/${id}`)
                                }
                                className="view-request-label"
                            >
                                View Request
                            </span>
                        </div>

                        <div className="request-meta">
                            <span>
                                <i className="fas fa-eye"></i>{" "}
                                {job.views || 0}
                            </span>
                        </div>
                    </div>

                    {/* MENU */}
                    <button
                        className="requests-ellipsis-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleMenu(job.id);
                        }}
                    >
                        <i className="fas fa-ellipsis-v"></i>
                    </button>

                    {activeMenuId === job.id && (
                        <div className="request-dropdown-menu">
                            <button
                                className="request-dropdown-item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    editRequest(job);
                                }}
                            >
                                Edit
                            </button>

                            <button
                                className="request-dropdown-item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteRequest(job.id);
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* LEADS SECTION (UNCHANGED) */}
            <h4 style={{ marginTop: "20px" }}>
                {allLeadsCount}{" "}
                {allLeadsCount === 1 ? "Response" : "Responses"}
            </h4>

            {/* LEADS SECTION */}
            <div className="leads-grid">
                {job?.leads?.map((lead) => (
                    <LeadCard
                        key={lead.id}
                        lead={lead}
                        job={job}   // 👈 full job passed if you need it
                        activeDropupId={activeDropupId}
                        setActiveDropupId={setActiveDropupId}
                        dropupForm={dropupForm}
                        setDropupForm={setDropupForm}
                        handleLeadAction={handleLeadAction}
                        handleDropupConfirm={handleDropupConfirm}
                    />
                ))}
            </div>
        </div>
    );
};

export default RequestDetails;